// src/pages/SettingPage/tabs/StudentManagementTab.js
// -----------------------------------------------------------------------------
// 学生管理タブ（CRUD・Excelインポート/エクスポート・検索/絞り込み）
// - Firestore: users / roles
// - Auth(Secondary): 追加時に Authentication ユーザーを作成
// - Cloud Functions(deleteUserAccount): 退会時の一括削除（Auth/Firestore/Storage）
// - 画像アップロード: Firebase Storage + 事前圧縮
// - テーブル: 検索・クラス絞り込み・並び替え・ページサイズ変更(10/20/50/100)
// - SWR: ローカルキャッシュ表示 → バックグラウンドで再取得
// すべて日本語のラベル／注記。ソースは ESLint の hooks ルールに準拠。
// -----------------------------------------------------------------------------

import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Typography,
  Table,
  Avatar,
  Space,
  Button,
  Input,
  Popconfirm,
  Modal,
  Form,
  Upload,
  message,
  Tooltip,
  Select,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import imageCompression from "browser-image-compression";
import * as XLSX from "xlsx";

import { db, storage } from "../../../firebase-config";
import {
  collection,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  query,
  where,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Secondary Auth（別アプリ名で初期化）
import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";

// Cloud Functions（Auth・Firestore・Storage を削除）
import { getFunctions, httpsCallable } from "firebase/functions";

// Excel 出力ユーティリティ（プロジェクト既存）
import { downloadExcelTemplate } from "../../../utils/excelUtils";

const { Title, Text } = Typography;
const { Search } = Input;

const CF_REGION = "asia-northeast2";
const PLACEHOLDER_EMAIL = "25013@toakeiri.com"; // Excel 更新専用：新規作成しない疑似メール

/** permissionKey を呑み込むラッパー（React 警告回避） */
const PermButton = ({ permissionKey, ...btnProps }) => <Button {...btnProps} />;

/* ------------------------------ キャッシュ ------------------------------ */
const CACHE_TTL_MS = 15 * 60 * 1000; // 15分
const loadCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};
const saveCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
};

/* -------------------------- Secondary Auth 取得 ------------------------- */
const getSecondaryAuth = () => {
  const defaultApp = getApp();
  const secondaryApp =
    getApps().find((a) => a.name === "Secondary") ||
    initializeApp(defaultApp.options, "Secondary");
  return getAuth(secondaryApp);
};

/* ------------------------------ サブフォーム ------------------------------ */
const StudentForm = ({ form, mode, roles }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const avatarValue = Form.useWatch("avatar", form);
  useEffect(() => {
    setImageUrl(avatarValue);
  }, [avatarValue]);

  const roleOptions = useMemo(
    () =>
      (Array.isArray(roles) ? roles : []).map((r) => ({
        value: r.id,
        label: r.name || r.displayName || r.title || r.code || r.id,
      })),
    [roles]
  );

  const handleImageUpload = async (file) => {
    const options = { maxSizeMB: 0.05, maxWidthOrHeight: 800 };
    try {
      message.loading({ content: "画像を処理中...", key: "uploading" });
      const compressed = await imageCompression(file, options);
      const path = `student-avatars/${file.name}_${Date.now()}`;
      const imageRef = ref(storage, path);
      await uploadBytes(imageRef, compressed);
      const url = await getDownloadURL(imageRef);
      form.setFieldsValue({ avatar: url, avatarPath: path });
      message.success({ content: "画像のアップロードに成功しました。", key: "uploading" });
    } catch {
      message.error({ content: "画像の処理に失敗しました。", key: "uploading" });
    }
    return false; // antd Upload のデフォ動作を止める
  };

  return (
    <Form form={form} layout="vertical" id="student_form">
      {/* 画像 */}
      <Form.Item label="プロフィール画像">
        <Space align="start">
          <Avatar size={64} src={imageUrl} icon={<UserOutlined />} />
          <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />}>画像をアップロード</Button>
          </Upload>
        </Space>
        <Text type="secondary">
          ※ Cloud Functions によりアカウント削除時は画像も自動削除。
        </Text>
      </Form.Item>
      <Form.Item name="avatar" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="avatarPath" hidden>
        <Input />
      </Form.Item>

      {/* 基本情報 */}
      <Form.Item
        name="studentId"
        label="学生ID"
        rules={[{ required: true, message: "IDを入力してください。" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="nameRomaji"
        label="氏名（ローマ字）"
        rules={[{ required: true, message: "氏名を入力してください。" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="nameKatakana"
        label="氏名（カタカナ）"
        rules={[{ required: true, message: "氏名を入力してください。" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="className"
        label="クラス"
        rules={[{ required: true, message: "クラスを入力してください。" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="regNumber"
        label="出席番号"
        rules={[{ required: true, message: "番号を入力してください。" }]}
      >
        <Input />
      </Form.Item>

      {/* 役割 */}
      <Form.Item
        name="roleId"
        label="役割"
        rules={[{ required: true, message: "役割を選択してください。" }]}
      >
        <Select placeholder="役割を選択" options={roleOptions} />
      </Form.Item>

      {/* ログイン情報 */}
      <Form.Item
        name="email"
        label="メールアドレス（ログインID）"
        rules={[{ required: true, type: "email", message: "メールアドレスが無効です。" }]}
      >
        <Input disabled={mode === "edit"} />
      </Form.Item>

      {mode === "add" && (
        <Form.Item
          name="password"
          label="パスワード"
          rules={[
            { required: true, message: "パスワードを入力してください。" },
            { min: 6, message: "パスワードは6文字以上で入力してください。" },
          ]}
        >
          <Input.Password />
        </Form.Item>
      )}
    </Form>
  );
};

/* =============================== 本体コンポーネント =============================== */
const StudentManagementTab = ({ roles }) => {
  // -------- state --------
  const [searchText, setSearchText] = useState("");
  const [classFilter, setClassFilter] = useState(null);

  const [users, setUsers] = useState([]);
  const [rolesLocal, setRolesLocal] = useState([]);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add | edit
  const [editingUser, setEditingUser] = useState(null);

  const [form] = Form.useForm();

  // Firestore refs（メモ化）
  const usersColRef = useMemo(() => collection(db, "users"), []);
  const rolesColRef = useMemo(() => collection(db, "roles"), []);

  /* -------- ページネーション（state を分離：ESLint 警告回避） -------- */
  const PAGE_SIZE_KEY = "students_page_size";
  const initialPageSize = Number(localStorage.getItem(PAGE_SIZE_KEY)) || 10;
  const [pageCurrent, setPageCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
  }, [pageSize]);

  /* ------------------------- roles の取得（SWR） ------------------------- */
  useEffect(() => {
    let cancelled = false;
    const show = (list) => !cancelled && setRolesLocal(list);

    const cached = loadCache("roles_cache_v1");
    if (cached) show(cached);

    const load = async () => {
      try {
        // まず 'roles'
        let snap = await getDocs(rolesColRef);
        let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // フォールバック：'role'
        if (!list.length) {
          snap = await getDocs(collection(db, "role"));
          list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }
        saveCache("roles_cache_v1", list);
        show(list);
      } catch (e) {
        console.error("[roles] load error:", e);
        message.error("役割一覧の読み込みに失敗しました。");
      }
    };

    if (Array.isArray(roles) && roles.length) {
      saveCache("roles_cache_v1", roles);
      show(roles);
    } else if (!cached || cached.length === 0) {
      load();
    }

    return () => {
      cancelled = true;
    };
  }, [roles, rolesColRef]);

  const rolesSource = useMemo(
    () => (Array.isArray(roles) && roles.length ? roles : rolesLocal),
    [roles, rolesLocal]
  );
  const rolesById = useMemo(
    () => new Map((rolesSource || []).map((r) => [String(r.id), r])),
    [rolesSource]
  );
  const rolesByName = useMemo(
    () =>
      new Map(
        (rolesSource || []).map((r) => [String((r.name || "").trim().toLowerCase()), r])
      ),
    [rolesSource]
  );
  const resolveRoleId = useCallback(
    (raw) => {
      if (!raw) return null;
      const s = String(raw).trim();
      if (rolesById.has(s)) return s;
      const byName = rolesByName.get(s.toLowerCase());
      return byName?.id || null;
    },
    [rolesById, rolesByName]
  );

  /* --------------------- users の取得（SWR + 互換） --------------------- */
  const enrich = useCallback(
    (u) => ({
      ...u,
      roleName:
        rolesById.get(String(u.roleId))?.name ||
        rolesByName.get(String(u.roleId || "").toLowerCase())?.name ||
        "未設定",
    }),
    [rolesById, rolesByName]
  );

  const netFetchUsers = useCallback(async () => {
    // 優先: userType==='student'
    const snap1 = await getDocs(query(usersColRef, where("userType", "==", "student")));
    if (!snap1.empty) return snap1.docs.map((d) => ({ id: d.id, ...d.data() }));

    // 互換: type==='student'
    const snap2 = await getDocs(query(usersColRef, where("type", "==", "student")));
    if (!snap2.empty) {
      message.info("フィールド 'userType' の代わりに 'type' を使用している可能性があります。");
      return snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
    }

    // サンプル読取（構造確認用）
    const any = await getDocs(query(usersColRef, limit(5)));
    if (!any.empty) {
      console.warn("users サンプル:", any.docs.map((d) => d.data()));
      message.warning("学生データが 'student' フィルターに一致しません。フィールド名/値をご確認ください。");
    }
    return [];
  }, [usersColRef]);

  const fetchUsers = useCallback(
    async (force = false) => {
      setLoading(true);
      try {
        let list = !force ? loadCache("students_cache_v1") : null;
        if (!list) {
          list = await netFetchUsers();
          saveCache("students_cache_v1", list);
        } else if (Array.isArray(list) && list.length === 0) {
          // 空キャッシュ → ネット確認
          const fresh = await netFetchUsers();
          saveCache("students_cache_v1", fresh);
          list = fresh;
        }
        setUsers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("[users] load error:", e);
        message.error("学生一覧を読み込めませんでした。");
      } finally {
        setLoading(false);
      }
    },
    [netFetchUsers]
  );

  useEffect(() => {
    const cached = loadCache("students_cache_v1");
    if (cached) setUsers(cached);
    fetchUsers(!cached);
  }, [fetchUsers]);

  useEffect(() => {
    saveCache("students_cache_v1", users);
  }, [users]);

  /* ----------------------- 検索/絞り込み/並び替え ----------------------- */
  const coll = useMemo(() => new Intl.Collator(["ja", "vi", "en"], { sensitivity: "base" }), []);
  const cmpStr = (a, b, key) => coll.compare(String(a?.[key] ?? ""), String(b?.[key] ?? ""));
  const cmpNumStr = (a, b, key) => {
    const pa = parseInt(String(a?.[key] ?? ""), 10);
    const pb = parseInt(String(b?.[key] ?? ""), 10);
    const na = Number.isNaN(pa) ? Number.POSITIVE_INFINITY : pa;
    const nb = Number.isNaN(pb) ? Number.POSITIVE_INFINITY : pb;
    return na - nb;
  };
  // クラス→出席 の優先ソート
  const sortByClassAndReg = useCallback(
    (a, b) => {
      const c = coll.compare(String(a?.className ?? ""), String(b?.className ?? ""));
      if (c !== 0) return c;
      const pa = parseInt(String(a?.regNumber ?? ""), 10);
      const pb = parseInt(String(b?.regNumber ?? ""), 10);
      const na = Number.isNaN(pa) ? Number.POSITIVE_INFINITY : pa;
      const nb = Number.isNaN(pb) ? Number.POSITIVE_INFINITY : pb;
      return na - nb;
    },
    [coll]
  );

  const filteredData = useMemo(() => {
    let arr = users;
    if (searchText) {
      const lower = searchText.toLowerCase();
      arr = arr.filter((item) =>
        Object.values(item).some((val) => String(val).toLowerCase().includes(lower))
      );
    }
    if (classFilter && classFilter.length > 0) {
      const allow = new Set(classFilter.map(String));
      arr = arr.filter((r) => allow.has(String(r.className || "")));
    }
    const enriched = arr.map(enrich);
    return enriched.sort(sortByClassAndReg);
  }, [users, searchText, classFilter, enrich, sortByClassAndReg]);

  const classFilters = useMemo(() => {
    const set = new Set(
      (users || []).map((u) => String(u.className || "").trim()).filter(Boolean)
    );
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ text: v, value: v }));
  }, [users]);

  const totalCount = users.length;
  const visibleCount = filteredData.length;

  // ページサイズと件数の整合（依存配列は分離した state を指定）
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (pageCurrent > maxPage) setPageCurrent(1);
  }, [filteredData.length, pageSize, pageCurrent]);

  /* ------------------------------ モーダル系 ------------------------------ */
  const showAddModal = () => {
    setModalMode("add");
    setEditingUser(null);
    form.resetFields();
    setIsModalOpen(true);
  };
  const showEditModal = (record) => {
    setModalMode("edit");
    setEditingUser(record);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  /* ------------------------------ 状態更新 ------------------------------ */
  const upsertUsersState = useCallback((upserts) => {
    setUsers((prev) => {
      const map = new Map(prev.map((u) => [u.id, u]));
      upserts.forEach((u) => {
        const old = map.get(u.id) || {};
        map.set(u.id, { ...old, ...u });
      });
      return Array.from(map.values());
    });
  }, []);

  /* ------------------------------- 追加/更新 ------------------------------ */
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const base = { ...values, userType: "student" };

      if (modalMode === "add") {
        // 学生IDの重複チェック（ローカル）
        const existsLocal = users.some(
          (u) => String(u.studentId).trim() === String(values.studentId).trim()
        );
        if (existsLocal) {
          form.setFields([{ name: "studentId", errors: ["この学生IDは既に存在します。"] }]);
          return;
        }

        try {
          // Auth作成（Secondary）
          const secondaryAuth = getSecondaryAuth();
          const cred = await createUserWithEmailAndPassword(
            secondaryAuth,
            String(values.email).trim().toLowerCase(),
            String(values.password).trim()
          );
          const uid = cred.user.uid;

          const { password, ...student } = base;
          const finalData = {
            ...student,
            email: String(values.email).trim().toLowerCase(),
            authUid: uid,
            roleId: resolveRoleId(student.roleId),
            avatar: student.avatar || `https://i.pravatar.cc/150?u=${uid}`,
            avatarPath: student.avatarPath || `default/pravatar/${uid}`,
          };

          await setDoc(doc(db, "users", uid), finalData);
          upsertUsersState([{ id: uid, ...finalData }]);
          message.success("学生を追加しました。");
        } catch (error) {
          console.error(error);
          if (error.code === "auth/email-already-in-use") {
            form.setFields([{ name: "email", errors: ["このメールアドレスは既に使用されています。"] }]);
          } else if (error.code === "auth/weak-password") {
            form.setFields([{ name: "password", errors: ["パスワードは6文字以上で入力してください。"] }]);
          } else {
            message.error(`エラー: ${error.code || "不明"}`);
          }
          return;
        }
      } else {
        // 更新
        const userRef = doc(db, "users", editingUser.id);
        const { email, password, ...updateData } = base;
        updateData.roleId = resolveRoleId(updateData.roleId);
        await updateDoc(userRef, updateData);
        upsertUsersState([{ id: editingUser.id, ...updateData }]);
        message.success("更新しました。");
      }

      handleCancel();
    } catch (err) {
      if (err?.errorFields) return; // バリデーションエラー
      console.error(err);
      message.error("保存中にエラーが発生しました。");
    }
  };

  /* -------------------------------- 削除 -------------------------------- */
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const user = users.find((u) => u.id === id);
      if (!user) {
        message.error("学生レコードが見つかりません。");
        setLoading(false);
        return;
      }
      const uid = user.authUid || id;
      const avatarPath = user.avatarPath || "";

      const functions = getFunctions(undefined, CF_REGION);
      const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");
      await deleteUserAccount({ authUid: uid, firestoreId: id, avatarPath });

      message.success("学生を削除しました（Auth・Firestore・Storage）。");
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error("deleteUserAccount error:", err);
      const code = String(err?.code || err?.message || "");
      if (code.includes("permission-denied")) {
        message.error("削除権限（管理者権限）がありません。権限付与後に再ログインしてください。");
      } else if (code.includes("unauthenticated")) {
        message.error("ログイン期限が切れました。再度ログインしてください。");
      } else {
        // Fallback: Firestore のみ削除
        try {
          await deleteDoc(doc(db, "users", id));
          message.warning("Firestore のプロフィールを削除しました。Authentication はコンソールでご確認ください。");
          setUsers((prev) => prev.filter((u) => u.id !== id));
        } catch (e2) {
          console.error(e2);
          message.error("学生の削除中にエラーが発生しました。");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------ Excel 取込 ------------------------------ */
  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const handleExcelUpload = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      setUploading(true);
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);

        // 既存 studentId をまとめて存在確認
        const studentIds = Array.from(
          new Set(rows.map((r) => String(r.studentId || "").trim()).filter(Boolean))
        );

        const existingByStudentId = new Map();
        const chunks = chunk(studentIds, 10);
        for (const ids of chunks) {
          const qExist = query(
            usersColRef,
            where("userType", "==", "student"),
            where("studentId", "in", ids)
          );
          const snap = await getDocs(qExist);
          snap.docs.forEach((d) => {
            const data = d.data();
            existingByStudentId.set(String(data.studentId).trim(), { id: d.id, data });
          });
        }

        const batch = writeBatch(db);
        const secondaryAuth = getSecondaryAuth();

        let created = 0,
          updated = 0,
          skipped = 0,
          failed = 0;
        const upsertsForState = [];

        for (const row of rows) {
          const email = String(row.email || "").trim().toLowerCase();
          const password = String(row.password || "").trim();
          const studentId = String(row.studentId || "").trim();
          if (!studentId) {
            failed++;
            continue;
          }

          const exist = existingByStudentId.get(studentId);
          if (exist) {
            // 既存 → 更新
            const docRef = doc(db, "users", exist.id);
            const updateData = {
              userType: "student",
              studentId,
              nameRomaji: String(row.nameRomaji || "").trim(),
              nameKatakana: String(row.nameKatakana || "").trim(),
              className: String(row.className || "").trim(),
              regNumber: String(row.regNumber || "").trim(),
              roleId: resolveRoleId(row.roleId ?? row.role ?? row.roleName ?? null),
            };
            batch.update(docRef, updateData);
            upsertsForState.push({ id: exist.id, ...updateData });
            updated++;
            continue;
          }

          // 新規作成条件
          if (!email || email === PLACEHOLDER_EMAIL || !password) {
            skipped++;
            continue;
          }

          try {
            const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
            const uid = cred.user.uid;
            const data = {
              userType: "student",
              studentId,
              nameRomaji: String(row.nameRomaji || "").trim(),
              nameKatakana: String(row.nameKatakana || "").trim(),
              className: String(row.className || "").trim(),
              regNumber: String(row.regNumber || "").trim(),
              email,
              roleId: resolveRoleId(row.roleId ?? row.role ?? row.roleName ?? null),
              authUid: uid,
              avatar: `https://i.pravatar.cc/150?u=${uid}`,
              avatarPath: `default/pravatar/${uid}`,
            };
            batch.set(doc(db, "users", uid), data);
            upsertsForState.push({ id: uid, ...data });
            created++;
          } catch (err) {
            failed++;
            console.error("Excel create user error:", email, err?.code || err?.message);
          }
        }

        await batch.commit();
        if (upsertsForState.length) upsertUsersState(upsertsForState);

        const parts = [];
        if (created) parts.push(`${created}件 新規`);
        if (updated) parts.push(`${updated}件 更新`);
        if (skipped) parts.push(`${skipped}件 スキップ`);
        if (failed) parts.push(`${failed}件 失敗`);
        message.success(`アップロード完了: ${parts.join("、")}。`);
      } catch (readErr) {
        console.error(readErr);
        message.error("Excel ファイルが不正です。");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsBinaryString(file);
    return false;
  };

  /* ------------------------------ Excel 書出 ------------------------------ */
  const handleDownloadTemplate = async () => {
    try {
      const getRoleName = (rid) =>
        rolesById.get(String(rid))?.name ||
        rolesByName.get(String(rid || "").toLowerCase())?.name ||
        "";

      const rows = [...users]
        .map(enrich)
        .sort(sortByClassAndReg)
        .map((d) => ({
          studentId: String(d.studentId || "").trim(),
          nameRomaji: String(d.nameRomaji || "").trim(),
          nameKatakana: String(d.nameKatakana || "").trim(),
          className: String(d.className || "").trim(),
          regNumber: String(d.regNumber || "").trim(),
          email: String(d.email || "").trim(),
          password: "",
          role: getRoleName(d.roleId) || d.roleName || "",
        }));

      const payload =
        rows.length > 0
          ? rows
          : [
              {
                studentId: "S0001",
                nameRomaji: "Yamada Taro",
                nameKatakana: "ヤマダ タロウ",
                className: "1A",
                regNumber: "001",
                email: "s0001@example.com",
                password: "password123",
                role: "学生",
              },
            ];

      downloadExcelTemplate(payload, "StudentUploadTemplate");
    } catch (e) {
      console.error(e);
      message.error("テンプレートの作成に失敗しました。");
    }
  };

  /* --------------------------------- Table -------------------------------- */
  const columns = [
    {
      title: "画像",
      dataIndex: "avatar",
      key: "avatar",
      render: (url) => <Avatar src={url} icon={<UserOutlined />} />,
    },
    {
      title: "学生ID",
      dataIndex: "studentId",
      key: "studentId",
      sorter: (a, b) => cmpStr(a, b, "studentId"),
    },
    {
      title: "氏名（ローマ字）",
      dataIndex: "nameRomaji",
      key: "nameRomaji",
      sorter: (a, b) => cmpStr(a, b, "nameRomaji"),
    },
    {
      title: "氏名（カタカナ）",
      dataIndex: "nameKatakana",
      key: "nameKatakana",
      sorter: (a, b) => cmpStr(a, b, "nameKatakana"),
    },
    {
      title: "クラス",
      dataIndex: "className",
      key: "className",
      filters: classFilters,
      filterSearch: true,
      filteredValue: classFilter || null,
      sorter: (a, b) => cmpStr(a, b, "className"),
    },
    {
      title: "出席",
      dataIndex: "regNumber",
      key: "regNumber",
      sorter: (a, b) => cmpNumStr(a, b, "regNumber"),
    },
    {
      title: "役割",
      dataIndex: "roleName",
      key: "roleName",
      sorter: (a, b) => cmpStr(a, b, "roleName"),
    },
    {
      title: "メール(Login ID)",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => cmpStr(a, b, "email"),
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <PermButton icon={<EditOutlined />} onClick={() => showEditModal(record)}>
            編集
          </PermButton>
          <Popconfirm
            title="学生を削除"
            description="削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <PermButton icon={<DeleteOutlined />} danger>
              削除
            </PermButton>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination, filters /*, sorter, extra */) => {
    setPageCurrent(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    setClassFilter(filters.className || null);
  };

  /* --------------------------------- Render -------------------------------- */
  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        学生管理{" "}
        <Text type="secondary" style={{ marginLeft: 8 }}>
          （表示 {visibleCount}/{totalCount}）
        </Text>
      </Title>

      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Search
          placeholder="検索..."
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <PermButton type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
          新規追加
        </PermButton>

        <Upload beforeUpload={handleExcelUpload} showUploadList={false} accept=".xlsx,.xls">
          <PermButton icon={<UploadOutlined />} loading={uploading}>
            {uploading ? "処理中..." : "Excel からアップロード"}
          </PermButton>
        </Upload>

        <PermButton icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          テンプレートをダウンロード（既存データから）
        </PermButton>

        <Tooltip
          title={`アップロードの注意:
- 学生IDが重複する行は更新扱い。
- メール ${PLACEHOLDER_EMAIL} は更新専用（Auth新規作成しない）。
- 新規作成は有効なメール・パスワード必須。`}
        >
          <InfoCircleOutlined style={{ cursor: "help", fontSize: 16 }} />
        </Tooltip>

        <PermButton icon={<ReloadOutlined />} onClick={() => fetchUsers(true)}>
          再読み込み（キャッシュ無視）
        </PermButton>
      </Space>

      <Table
        columns={columns}
        dataSource={filteredData.map((s) => ({ ...s, key: s.id }))}
        bordered
        loading={loading}
        pagination={{
          current: pageCurrent,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
        }}
        onChange={handleTableChange}
        locale={{ emptyText: loading ? "読み込み中..." : "No data" }}
      />

      <Modal
        title={modalMode === "add" ? "学生を新規追加" : "情報を更新"}
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={handleOk}
        okText={modalMode === "add" ? "追加" : "更新"}
        cancelText="キャンセル"
        forceRender
        destroyOnClose
      >
        <StudentForm form={form} mode={modalMode} roles={rolesSource} />
      </Modal>
    </div>
  );
};

export default StudentManagementTab;
